import React, { useEffect, useState } from "react";
import { Alert, Box, Container, Typography } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import PlansSection from "./JoinPageSections/PlansSection";
import VerificationSection from "./JoinPageSections/VerificationSection";
import JoinAnchorRail from "./JoinPageSections/JoinAnchorRail";
import { useJoinMotion } from "./JoinPageSections/JoinMotion";

const TIER_FLAT_TEXT = {
  fontSize: { xs: 13, md: 14 },
  fontWeight: 600,
  color: "var(--black-color)",
  lineHeight: 1.4,
};

export default function JoinPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const billingCancelled = searchParams.get("billing") === "cancelled";
  const [billingNotice, setBillingNotice] = useState(billingCancelled);
  const { hero } = useJoinMotion();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const raw = location.hash?.replace("#", "");
    if (!raw) return undefined;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      const el = document.getElementById(raw);
      if (el) {
        el.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "start" });
      }
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  const dismissBillingNotice = () => {
    setBillingNotice(false);
    const next = new URLSearchParams(searchParams);
    next.delete("billing");
    setSearchParams(next, { replace: true });
  };

  return (
    <Box
      component="main"
      sx={{
        bgcolor: "var(--root-background)",
        backgroundImage:
          "radial-gradient(900px circle at 20% -10%, var(--secondary-bg-color-light), transparent 50%)",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 6 } }}>
        <Box
          component={motion.div}
          {...hero}
          sx={{ mb: 2, width: "100%", textAlign: "center" }}
        >
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              color: "var(--black-color)",
              fontSize: { xs: 30, md: 44 },
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            CivixThread Features
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              rowGap: 0.5,
              columnGap: 0,
              mb: 2.5,
            }}
          >
            <Box component="span" sx={TIER_FLAT_TEXT}>
              Forever free for citizens
            </Box>
            <Box
              component="span"
              aria-hidden
              sx={{
                mx: { xs: 1.25, md: 1.75 },
                color: "var(--dna-color-palette-black-400)",
                fontSize: 14,
                fontWeight: 400,
                userSelect: "none",
              }}
            >
              |
            </Box>
            <Box
              component="span"
              sx={{
                ...TIER_FLAT_TEXT,
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
                justifyContent: "center",
              }}
            >
              Organizer Plan
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: "999px",
                  bgcolor: "var(--primary-color)",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.01em",
                }}
              >
                $99/mo first year
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--dna-color-palette-black-400)",
                }}
              >
                then $149/mo
              </Box>
            </Box>
          </Box>

          {billingNotice && (
            <Alert severity="info" onClose={dismissBillingNotice} sx={{ mb: 1.5, textAlign: "left" }}>
              Checkout was cancelled. You can enroll in the Organizer Plan when you&apos;re ready.
            </Alert>
          )}
        </Box>

        <JoinAnchorRail />

        <PlansSection />
        <VerificationSection />
      </Container>
    </Box>
  );
}
